import { inject, InjectionToken, Provider, TemplateRef, ViewContainerRef } from '@angular/core';

import { Observable } from 'rxjs';

import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  OverlayPosition,
} from '../../../../core/overlay/overlay-directives';

export interface OpenStrategyOptions {
  overlayWidth?: number | string;
  overlayMinWidth?: number | string;
  overlayPosition?: OverlayPosition[];
  autoClose?: boolean;
}

export interface RegisterOptions {
  inlineTriggerOrigin: OverlayOriginDirective;
  dropdownConnectedOverlay: ConnectedOverlayDirective;
  dropdownTemplate: TemplateRef<void>;
  dropdownTemplateVcRef: ViewContainerRef;
  triggerOverlay: ConnectedOverlayDirective;
  overlayTriggerOrigin$: Observable<OverlayOriginDirective | undefined>;
}

export abstract class OpenStrategy {
  protected inlineTriggerOrigin: OverlayOriginDirective;
  protected dropdownConnectedOverlay: ConnectedOverlayDirective;
  protected dropdownTemplate: TemplateRef<void>;
  protected dropdownTemplateVcRef: ViewContainerRef;
  protected triggerOverlay: ConnectedOverlayDirective;
  protected overlayTriggerOrigin$: Observable<OverlayOriginDirective | undefined>;
  protected options: OpenStrategyOptions = {};

  // Called by select to register overlays
  public register(options: RegisterOptions) {
    Object.assign(this, options);
  }

  public setOptions(options: OpenStrategyOptions) {
    this.options = options;
  }

  // Called after overlays registered
  public abstract init(): void;
}

export enum StrategyRelevance {
  Default,
  Feature,
  Custom,
}

interface OpenStrategyProvider {
  strategy: new () => OpenStrategy;
  relevance: StrategyRelevance;
}

const OPEN_STRATEGY = new InjectionToken<OpenStrategyProvider[]>('OPEN_STRATEGY');

export const provideOpenStrategy = (
  strategy: new () => OpenStrategy,
  relevance: StrategyRelevance,
): Provider => ({
  provide: OPEN_STRATEGY,
  useValue: <OpenStrategyProvider>{ strategy, relevance },
  multi: true,
});

export const injectOpenStrategy = () => {
  const provider = inject(OPEN_STRATEGY).reduce((acc, provider) => {
    if (acc.relevance === provider.relevance) {
      console.warn('Multiple strategies with the same relevance found. Using the last one.');
    }
    return acc.relevance > provider.relevance ? acc : provider;
  });

  return new provider.strategy();
};
